import { gql } from "lib/apolloClient";
import {
  likefragment,
  journalPostFragment,
  attachmentGroupsFragmentForStudentAssignment,
} from "./PostFragments";

export const createLikeMutation = gql`
  mutation createLike(
    $userId: ID!
    $parentId: ID!
    $parentType: PARENT_TYPE_ENUM!
  ) {
    documentation {
      createLike(
        input: { userId: $userId, parentId: $parentId, parentType: $parentType }
      ) {
        totalCount
        edges {
          node {
            ...likeItem
          }
        }
      }
    }
  }
  ${likefragment.like}
`;

export const deleteLikeMutation = gql`
  mutation deleteLike(
    $userId: ID!
    $parentId: ID!
    $parentType: PARENT_TYPE_ENUM!
  ) {
    documentation {
      deleteLike(
        input: { userId: $userId, parentId: $parentId, parentType: $parentType }
      ) {
        totalCount
        edges {
          node {
            ...likeItem
          }
        }
      }
    }
  }
  ${likefragment.like}
`;

export const deletePostMutation = gql`
  mutation deletePost($id: ID!) {
    documentation {
      deletePost(id: $id) {
        isSuccess
      }
    }
  }
`;

export const updatePostMutation = gql`
  mutation updatePost(
    $postId: ID!
    $courseId: ID
    $title: String
    $description: String
    $updatedBy: ID!
    $unitPlanId: ID
    $resourceId: ID
    $resourceType: FLOW_SECTION_ITEM_ENUM
    $elementsPYP: [PYPElement!]
    $studentIds: [ID!]
    $state: PostStateEnum!
    $isPrivate: Boolean
    $isHiddenFromStudents: Boolean
    $attachments: [AttachmentInput!]
    $evaluationCycleId: ID
    $addedFolders: [ID!]
    $addedFolderNames: [String!]
    $removedFolders: [ID!]
    $addedPractices: [ID!]
    $removedPractices: [ID!]
    $evidenceStatus: SNP_EVIDENCE_STATUS_ENUM
    $approvComment: String
    $rejectComment: String
    $newAddedRatings: [IBPYPElementRatingInput!]
    $removedRatings: [DeleteIBPYPElementRatingsInput!]
    $isStudentIndividuallyRated: Boolean
    $addAttachments: [AttachmentInput!]
    $removeAttachmentIds: [ID!]
    $studentActivities: UpdateStudentActivityInput
    $publishedAt: String
    $academicTermId: ID
    $createFolders: [FolderInput!]
    $taggedEntity: UpdateTaggedEntityInput
    $feedType: POST_FEED_TYPE_ENUM = PORTFOLIO
  ) {
    documentation {
      updatePost(
        input: {
          postId: $postId
          courseId: $courseId
          title: $title
          description: $description
          updatedBy: $updatedBy
          unitPlanId: $unitPlanId
          resourceId: $resourceId
          resourceType: $resourceType
          elementsPYP: $elementsPYP
          studentIds: $studentIds
          state: $state
          isPrivate: $isPrivate
          isHiddenFromStudents: $isHiddenFromStudents
          attachments: $attachments
          folders: {
            addedFolders: $addedFolders
            removedFolders: $removedFolders
            addedFolderNames: $addedFolderNames
            createFolders: $createFolders
          }
          practices: {
            evaluationCycleId: $evaluationCycleId
            addedPractices: $addedPractices
            removedPractices: $removedPractices
            evidenceStatus: $evidenceStatus
          }
          approvComment: $approvComment
          rejectComment: $rejectComment
          newAddedRatings: $newAddedRatings
          removedRatings: $removedRatings
          isStudentIndividuallyRated: $isStudentIndividuallyRated
          addAttachments: $addAttachments
          removeAttachmentIds: $removeAttachmentIds
          studentActivities: $studentActivities
          publishedAt: $publishedAt
          academicTermId: $academicTermId
          taggedEntity: $taggedEntity
          feedType: $feedType
        }
      ) {
        ...postItem
      }
    }
  }
  ${journalPostFragment.journalPost}
`;

export const createPostMutation = gql`
  mutation createPost(
    $courseId: ID!
    $title: String
    $description: String
    $createdBy: ID!
    $unitPlanId: ID
    $resourceId: ID
    $resourceType: FLOW_SECTION_ITEM_ENUM
    $elementsPYP: [PYPElement!]
    $attachments: [AttachmentInput!]
    $studentIds: [ID!]
    $state: PostStateEnum!
    $publishedBy: ID!
    $isPrivate: Boolean
    $isHiddenFromStudents: Boolean
    $folders: [ID!]
    $folderNames: [String!]
    $practicesV2: PostPracticeCreateInput
    $curriculumProgramId: ID
    $parentId: ID
    $parentType: POST_ITEM_TYPE_ENUM
    $academicYears: [ID!]
    $ibPYPElementRatings: [IBPYPElementRatingInput!]
    $isStudentIndividuallyRated: Boolean
    $publishedAt: String
    $studentActivities: [StudentActivityCreateInput!]
    $academicTermId: ID
    $createFolders: [FolderInput!]
    $activityLog: ActivityLogInput
    $taggedEntity: [TaggedEntityGenericInput!]
    $feedType: POST_FEED_TYPE_ENUM = PORTFOLIO
  ) {
    documentation {
      createPost(
        input: {
          courseId: $courseId
          title: $title
          description: $description
          createdBy: $createdBy
          unitPlanId: $unitPlanId
          resourceId: $resourceId
          resourceType: $resourceType
          elementsPYP: $elementsPYP
          attachments: $attachments
          studentIds: $studentIds
          state: $state
          publishedBy: $publishedBy
          isPrivate: $isPrivate
          isHiddenFromStudents: $isHiddenFromStudents
          folders: $folders
          folderNames: $folderNames
          practicesV2: $practicesV2
          curriculumProgramId: $curriculumProgramId
          parentId: $parentId
          parentType: $parentType
          academicYears: $academicYears
          ibPYPElementRatings: $ibPYPElementRatings
          isStudentIndividuallyRated: $isStudentIndividuallyRated
          publishedAt: $publishedAt
          studentActivities: $studentActivities
          academicTermId: $academicTermId
          createFolders: $createFolders
          activityLog: $activityLog
          taggedEntity: $taggedEntity
          feedType: $feedType
        }
      ) {
        id
      }
    }
  }
`;

export const bulkPostsActionMutation = gql`
  mutation bulkPostsAction($input: PostBulkActionInput!) {
    documentation {
      bulkPostsAction(input: $input)
    }
  }
`;

export const updateAssignmentToJournalMutation = gql`
  mutation updateAssignmentToJournal(
    $assignmentId: ID!
    $studentAssignmentIds: [ID!]
    $postData: PostCompleteInput
  ) {
    platform {
      updateAssignmentToJournal(
        input: {
          assignmentId: $assignmentId
          studentAssignmentIds: $studentAssignmentIds
          postData: $postData
        }
      ) {
        response
        warning
      }
    }
  }
`;

export const addSeenMutation = gql`
  mutation addSeen($input: SeenInput!) {
    platform {
      addSeen(input: $input) {
        totalCount
        edges {
          node {
            id
          }
        }
      }
    }
  }
`;

export const updatePostDateMutation = gql`
  mutation updatePostDate(
    $postId: ID!
    $updatedBy: ID!
    $state: PostStateEnum!
    $createdAt: String
    $publishedAt: String
  ) {
    documentation {
      updatePost(
        input: {
          postId: $postId
          updatedBy: $updatedBy
          state: $state
          createdAt: $createdAt
          publishedAt: $publishedAt
        }
      ) {
        ...postItem
      }
    }
  }
  ${journalPostFragment.journalPost}
`;

export const createAIConversationMutation = gql`
  mutation createAIConversationMutation($input: CreateAiConversationInput!) {
    ai {
      createAiConversation(input: $input) {
        isSuccess
        response {
          id
          entityType
        }
      }
    }
  }
`;

export const updateAiConversationMutation = gql`
  mutation updateAiConversationMutation($input: UpdateAiConversationInput!) {
    ai {
      updateAiConversation(input: $input) {
        isSuccess
      }
    }
  }
`;

export const bulkCourseFoldersActionsMutation = gql`
  mutation bulkCourseFoldersActions($input: BulkFolderActionInput!) {
    documentation {
      bulkFolderAction(input: $input) {
        isSuccess
        warning
      }
    }
  }
`;

export const createAttachmentGroupsMutation = gql`
  mutation createAttachmentGroups($input: [AttachmentGroupInput!]) {
    platform {
      createAttachmentGroups(input: $input) {
        ...attachmentGroupItem
      }
    }
  }
  ${attachmentGroupsFragmentForStudentAssignment.attachmentGroup}
`;
